import {
  DEFAULT_COMMISSION_BILL_TO_PRINT_OPTIONS,
  normalizeCommissionBillToPrintOptions,
  readBillToPrintOptionsFromSnapshot,
} from "./commissionBillToUtils";

export const DEFAULT_INVOICE_DISPLAY_OPTIONS = {
  header: { showLogo: true },
  to: {
    showSection: true,
    showName: true,
    showEmail: true,
    showPhone: true,
    showCountry: true,
    showCampus: true,
    showBillingAddress: true,
  },
  from: {
    showSection: true,
    showName: true,
    showPhone: true,
    showEmail: true,
    showWebsite: true,
    showAddress: true,
  },
  details: {
    showSection: true,
    showIssueDate: true,
    showInvoiceNumber: true,
    showReference: true,
    showDueDate: true,
    showPaymentDate: true,
    showAbn: true,
  },
  footer: {
    showPaymentAdvice: true,
    showTerms: true,
    showPaymentTerms: true,
    showAdviceAccountName: true,
    showAdviceBank: true,
    showAdviceBsb: true,
    showAdviceAccountNumber: true,
    showAdviceReference: true,
    showAdviceContact: true,
  },
};

const DEFAULT_FOOTER_FLAGS = DEFAULT_INVOICE_DISPLAY_OPTIONS.footer;

function boolGroup(defaults, raw) {
  const out = { ...defaults };
  if (!raw || typeof raw !== "object") return out;
  Object.keys(defaults).forEach((key) => {
    if (raw[key] !== undefined) {
      out[key] = raw[key] !== false;
    }
  });
  return out;
}

export function normalizeInvoiceDisplayOptions(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      header: { ...DEFAULT_INVOICE_DISPLAY_OPTIONS.header },
      to: { ...DEFAULT_INVOICE_DISPLAY_OPTIONS.to },
      from: { ...DEFAULT_INVOICE_DISPLAY_OPTIONS.from },
      details: { ...DEFAULT_INVOICE_DISPLAY_OPTIONS.details },
      footer: { ...DEFAULT_INVOICE_DISPLAY_OPTIONS.footer },
    };
  }
  return {
    header: boolGroup(DEFAULT_INVOICE_DISPLAY_OPTIONS.header, raw.header),
    to: boolGroup(DEFAULT_INVOICE_DISPLAY_OPTIONS.to, raw.to),
    from: boolGroup(DEFAULT_INVOICE_DISPLAY_OPTIONS.from, raw.from),
    details: boolGroup(DEFAULT_INVOICE_DISPLAY_OPTIONS.details, raw.details),
    footer: boolGroup(DEFAULT_FOOTER_FLAGS, raw.footer),
  };
}

export function areInvoiceDisplayOptionsDefault(displayOptions) {
  const normalized = normalizeInvoiceDisplayOptions(displayOptions);
  const defaults = defaultInvoiceDisplayOptions();
  return JSON.stringify(normalized) === JSON.stringify(defaults);
}

/** Map unified `to` group to legacy bill-to print shape (no showSection/showName). */
export function toBillToPrintOptionsFromDisplay(displayOptions) {
  const normalized = normalizeInvoiceDisplayOptions(displayOptions);
  const to = normalized.to;
  return normalizeCommissionBillToPrintOptions({
    showEmail: to.showEmail,
    showPhone: to.showPhone,
    showCountry: to.showCountry,
    showCampus: to.showCampus,
    showBillingAddress: to.showBillingAddress,
  });
}

/** Full To-party flags for preview/PDF builders (includes showName / showSection). */
export function toCommissionPartyPrintOptions(displayOptions) {
  const normalized = normalizeInvoiceDisplayOptions(displayOptions);
  return {
    ...toBillToPrintOptionsFromDisplay(normalized),
    showName: normalized.to.showName,
    showSection: normalized.to.showSection,
  };
}

export function readInvoiceDisplayOptionsFromSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return normalizeInvoiceDisplayOptions(null);
  }
  if (
    snapshot.invoice_display_options &&
    typeof snapshot.invoice_display_options === "object"
  ) {
    return normalizeInvoiceDisplayOptions(snapshot.invoice_display_options);
  }
  const legacy = readBillToPrintOptionsFromSnapshot(snapshot);
  return normalizeInvoiceDisplayOptions({
    to: {
      ...DEFAULT_INVOICE_DISPLAY_OPTIONS.to,
      showEmail: legacy.showEmail,
      showPhone: legacy.showPhone,
      showCountry: legacy.showCountry,
      showCampus: legacy.showCampus,
      showBillingAddress: legacy.showBillingAddress,
    },
  });
}

export function mergeInvoiceDisplayOptionsIntoSnapshot(snapshot, displayOptions) {
  const base = snapshot && typeof snapshot === "object" ? { ...snapshot } : {};
  const normalized = normalizeInvoiceDisplayOptions(displayOptions);
  return {
    ...base,
    invoice_display_options: normalized,
    bill_to_print_options: toBillToPrintOptionsFromDisplay(normalized),
  };
}

export function applyProviderOnlyPreset() {
  return normalizeInvoiceDisplayOptions({
    header: { showLogo: false },
    to: {
      showSection: true,
      showName: true,
      showEmail: false,
      showPhone: false,
      showCountry: false,
      showCampus: false,
      showBillingAddress: false,
    },
    from: {
      showSection: false,
      showName: false,
      showPhone: false,
      showEmail: false,
      showWebsite: false,
      showAddress: false,
    },
    details: {
      showSection: true,
      showIssueDate: false,
      showInvoiceNumber: true,
      showReference: false,
      showDueDate: true,
      showPaymentDate: false,
      showAbn: false,
    },
    footer: {
      showPaymentAdvice: false,
      showTerms: false,
      showPaymentTerms: false,
      showAdviceAccountName: false,
      showAdviceBank: false,
      showAdviceBsb: false,
      showAdviceAccountNumber: false,
      showAdviceReference: false,
      showAdviceContact: false,
    },
  });
}

export function defaultInvoiceDisplayOptions() {
  return normalizeInvoiceDisplayOptions(null);
}
